import { useState } from "react";
import {
  isApiError,
  useAcceptInvitation,
  useOrganizationInvitations,
  useRejectInvitation,
  useSendInvitation,
} from "../api";
import type { OrganizationInvitation } from "../types";

interface InvitationManagerProps {
  organizationId: string;
  /** AM-025 Addendum §6: send/accept/reject are all REJECTED once the
   * organization is archived — the parent page passes this down instead of
   * each component re-deriving it from a possibly-stale org object. */
  organizationArchived: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  pending: "در انتظار",
  accepted: "پذیرفته‌شده",
  rejected: "ردشده",
  expired: "منقضی‌شده",
  revoked: "لغوشده (سازمان آرشیو شد)",
};

export function InvitationManager({ organizationId, organizationArchived }: InvitationManagerProps) {
  const { data: invitations, isLoading } = useOrganizationInvitations(organizationId);
  const sendInvitation = useSendInvitation(organizationId);
  const acceptInvitation = useAcceptInvitation(organizationId);
  const rejectInvitation = useRejectInvitation(organizationId);
  const [email, setEmail] = useState("");

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    // AM-025 Addendum §10: invited_by is never sent — the server always uses
    // the authenticated user. Only the invitee's email goes in the payload.
    sendInvitation.mutate({ email }, { onSuccess: () => setEmail("") });
  };

  const sendErrorMessage = isApiError(sendInvitation.error)
    ? sendInvitation.error.response.data.detail
    : undefined;

  return (
    <div>
      <h3 className="font-medium mb-2">دعوت‌نامه‌های سازمان</h3>

      {organizationArchived ? (
        <p className="text-xs text-gray-400 mb-3">
          این سازمان آرشیو شده است — ارسال دعوت‌نامهٔ جدید ممکن نیست.
        </p>
      ) : (
        <form onSubmit={handleSend} className="flex gap-2 mb-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ایمیل مدعو"
            className="border rounded px-2 py-1 text-sm flex-1"
            aria-label="ایمیل برای دعوت"
          />
          <button
            type="submit"
            disabled={sendInvitation.isPending}
            className="px-3 py-1 rounded bg-blue-600 text-white text-sm disabled:opacity-50"
          >
            ارسال دعوت
          </button>
        </form>
      )}

      {sendInvitation.isError && (
        <p className="text-xs text-red-500 mb-2">
          {sendErrorMessage || "ارسال دعوت ناموفق بود — دسترسی «tenant.manage» لازم است."}
        </p>
      )}

      {isLoading && <p className="text-sm text-gray-500">در حال بارگذاری دعوت‌نامه‌ها...</p>}

      <ul className="divide-y">
        {invitations?.map((inv: OrganizationInvitation) => (
          <li key={inv.id} className="flex items-center justify-between py-1 text-sm">
            <span>
              {inv.email}{" "}
              <span className="text-gray-400">
                ({STATUS_LABEL[inv.status] ?? inv.status}
                {inv.status === "pending" && inv.is_expired ? " — منقضی" : ""})
              </span>
            </span>
            {inv.status === "pending" && !inv.is_expired && (
              <span className="flex gap-2">
                <button
                  type="button"
                  onClick={() => acceptInvitation.mutate(inv.id)}
                  className="text-green-600 text-xs hover:underline"
                >
                  پذیرش
                </button>
                <button
                  type="button"
                  onClick={() => rejectInvitation.mutate(inv.id)}
                  className="text-red-500 text-xs hover:underline"
                >
                  رد
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>

      <p className="text-xs text-gray-400 mt-2">
        پذیرش/رد فقط برای صاحب واقعی ایمیل دعوت‌نامه انجام می‌شود — سرور این را از
        نشست ورود شما تشخیص می‌دهد، نه از هیچ مقداری که این صفحه ارسال کند.
      </p>
    </div>
  );
}
